'use no memo';

import {useRef, useState} from 'react';

import {isNil} from 'ramda';

// Fallback for a dropped or failure-path copy-finished push.
const COPY_PLACEHOLDER_TIMEOUT_MS = 30000;

const SINGLE_PHOTO = 1;

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

/**
 * "Copying" placeholder tiles per destination album, one per photo being copied.
 *
 * The backend sends exactly one `vehicle_photo-copy_finished` push per copy request, carrying
 * no photo ids or count — so each request is tracked as a group holding its photo count, and
 * groups are resolved oldest-first to keep concurrent copies into one album independent.
 */
export const useAlbumCopyPlaceholders = () => {
  // Photo counts of in-flight copy requests per album, oldest first.
  const [pendingCopyGroupsByAlbum, setPendingCopyGroupsByAlbum] = useState<
    Record<string, number[]>
  >({});

  // One timeout per group, oldest first.
  const timeoutIdsByAlbum = useRef<Map<string, ReturnType<typeof setTimeout>[]>>(new Map());

  const dropOldestGroup = (destinationAlbumId: string) =>
    setPendingCopyGroupsByAlbum((prev) => {
      const [, ...remaining] = prev[destinationAlbumId] ?? [];
      const next = {...prev};
      if (remaining.length > 0) {
        next[destinationAlbumId] = remaining;
      } else {
        delete next[destinationAlbumId];
      }
      return next;
    });

  const removeTimeoutId = (
    destinationAlbumId: string,
    timeoutId: ReturnType<typeof setTimeout>
  ) => {
    const ids = timeoutIdsByAlbum.current.get(destinationAlbumId) ?? [];
    timeoutIdsByAlbum.current.set(
      destinationAlbumId,
      ids.filter((id) => id !== timeoutId)
    );
  };

  const markCopyStarted = (destinationAlbumId: string, photoCount: number = SINGLE_PHOTO) => {
    setPendingCopyGroupsByAlbum((prev) => ({
      ...prev,
      [destinationAlbumId]: [...(prev[destinationAlbumId] ?? []), photoCount],
    }));

    const timeoutId = setTimeout(() => {
      removeTimeoutId(destinationAlbumId, timeoutId);
      dropOldestGroup(destinationAlbumId);
    }, COPY_PLACEHOLDER_TIMEOUT_MS);

    const ids = timeoutIdsByAlbum.current.get(destinationAlbumId) ?? [];
    timeoutIdsByAlbum.current.set(destinationAlbumId, [...ids, timeoutId]);
  };

  const markCopyFinished = (destinationAlbumId: string) => {
    const ids = timeoutIdsByAlbum.current.get(destinationAlbumId) ?? [];
    const [oldest, ...rest] = ids;
    // Already timed out — nothing to settle.
    if (isNil(oldest)) {
      return;
    }
    clearTimeout(oldest);
    timeoutIdsByAlbum.current.set(destinationAlbumId, rest);
    dropOldestGroup(destinationAlbumId);
  };

  const getPendingCopyCount = (destinationAlbumId: string) =>
    sum(pendingCopyGroupsByAlbum[destinationAlbumId] ?? []);

  return {markCopyStarted, markCopyFinished, getPendingCopyCount};
};
